import type {
  PartialBatchUpdateSubscriptions,
  ProviderImportProfile,
  SubscribableResource,
  SubscribableResourceBase,
} from '@timelyapp/integration-cloud-api-spec';
import {
  Box,
  Button,
  Combobox,
  type ComboboxCustomItemProps,
  type DropdownMenuItem,
  Modal,
  Pill,
  Settings,
  Switch,
  Text,
} from '@timelyapp/react';
import { keyBy } from 'lodash';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { getResourceContextLabel } from '../../utils/util.profileLabels';

interface SubscriptionManagerModalProps {
  resource: SubscribableResource;
  isOpen: boolean;
  onClose: () => void;
  globalChanges: Record<string, PartialBatchUpdateSubscriptions>;
  onConfirm: (changes: Record<string, PartialBatchUpdateSubscriptions>) => void;
  activeProfile?: ProviderImportProfile;
}

const SubscriptionItem: React.FC<ComboboxCustomItemProps> = memo(({ item }) => {
  if (item.type !== 'item') return null;
  const hasSubscription = 'subscription' in item && item.subscription;
  if (!hasSubscription) {
    console.warn('Invalid item structure for SubscriptionItem:', item);
    // return null;
  }

  const subscription = (item as any)?.subscription as SubscribableResourceBase & {
    isEnabled: boolean;
  };
  return (
    <Box.Row width="100%" gap="medium" alignItems="center">
      {hasSubscription ? (
        <Switch
          size="small"
          checked={subscription.isEnabled}
          onChangeChecked={() => {}}
          data-testid={`subscription-switch-${subscription.subscription_id}`}
        />
      ) : (
        item.icon || null
      )}
      <Text.Label as="span" size="small">
        {item.label}
      </Text.Label>
      <Box.Spacer />
      {item.description && (
        <Text.Label size="xsmall" color="subtle">
          {item.description}
        </Text.Label>
      )}
    </Box.Row>
  );
});

SubscriptionItem.displayName = 'SubscriptionItem';

export const IntegrationsSetupWizardStepSubscriptionManagerModal: React.FC<
  SubscriptionManagerModalProps
> = ({ resource, isOpen, onClose, globalChanges, onConfirm, activeProfile }) => {
  // Local state for modal-only changes
  const [localChanges, setLocalChanges] = useState<Record<string, PartialBatchUpdateSubscriptions>>(
    {}
  );

  // Compute current state for each subscription combining global and local changes
  // Create proper copies to avoid mutating read-only RTK Query cache objects
  const subscriptionStates = useMemo(() => {
    if (!resource.sub_subscriptions?.length) return {};

    // Create a mutable copy of each subscription
    const states = keyBy(
      resource.sub_subscriptions.map(sub => ({ ...sub })),
      'subscription_id'
    );

    // Apply global changes first, then local changes in a single pass
    const allChanges = { ...globalChanges, ...localChanges };

    for (const change of Object.values(allChanges)) {
      const state = states[change.subscription_id];
      if (state) {
        state.enabled = change.enabled;
      }
    }

    return states;
  }, [resource.sub_subscriptions, globalChanges, localChanges]);

  // Memoize enabled and total counts separately to avoid recalculating unnecessarily
  const { enabledCount, totalCount } = useMemo(() => {
    const states = Object.values(subscriptionStates);
    return {
      enabledCount: states.filter(sub => sub.enabled).length,
      totalCount: states.length,
    };
  }, [subscriptionStates]);

  const resourceContextLabel = useMemo(
    () => getResourceContextLabel(resource, activeProfile),
    [resource, activeProfile]
  );

  // Memoize toggle all icon to prevent recreating on every render
  const toggleAllIcon = useMemo(
    () => (
      <div style={{ pointerEvents: 'none' }}>
        <Switch size="small" checked={enabledCount === totalCount} onChangeChecked={() => {}} />
      </div>
    ),
    [enabledCount, totalCount]
  );

  // Create combobox items from sub-subscriptions
  const comboboxItems = useMemo(() => {
    const subscriptionItems = Object.values(subscriptionStates).map(subscription => {
      const contextLabel = getResourceContextLabel(subscription as any, activeProfile);

      return {
        type: 'item' as const,
        value: subscription.subscription_id,
        label: subscription.title,
        description: contextLabel ?? subscription.external_type,
        // TODO: This whole thing stinks, but it's a start
        // @ts-expect-error Yeye, will fix on next iteration
        subscription: {
          ...subscription,
          isEnabled: subscription.enabled,
        },
      } satisfies DropdownMenuItem;
    });

    return [
      {
        type: 'item' as const,
        value: 'toggle-all',
        label: enabledCount === totalCount ? 'Disable all' : 'Enable all',
        icon: toggleAllIcon,
        description: `Toggle all subscriptions (${enabledCount} of ${totalCount} currently enabled)`,
      } satisfies DropdownMenuItem,
      // { type: 'separator' as const },
      ...subscriptionItems,
    ];
  }, [subscriptionStates, enabledCount, totalCount, toggleAllIcon, activeProfile]);

  const handleToggleSubscription = useCallback(
    (subscriptionId: string, enabled: boolean) => {
      const subscription = subscriptionStates[subscriptionId];
      if (!subscription) return;

      // Find the original subscription to determine the baseline state
      const originalSub = resource.sub_subscriptions?.find(
        sub => sub.subscription_id === subscriptionId
      );

      if (!originalSub) return;

      // Calculate the current effective state (original + any global changes)
      const currentEffectiveState = globalChanges[subscriptionId]?.enabled ?? originalSub.enabled;

      setLocalChanges(prev => {
        const updated = { ...prev };

        // If the new state matches the current effective state, remove the local change
        if (enabled === currentEffectiveState) {
          delete updated[subscriptionId];
        } else {
          // Otherwise, track the change
          updated[subscriptionId] = {
            subscription_id: subscriptionId,
            resource_id: subscription.resource_id,
            enabled,
          };
        }

        return updated;
      });
    },
    [subscriptionStates, resource.sub_subscriptions, globalChanges]
  );

  const handleToggleAll = useCallback(() => {
    const enabledCount = Object.values(subscriptionStates).filter(sub => sub.enabled).length;
    const totalCount = Object.keys(subscriptionStates).length;
    const shouldEnableAll = enabledCount < totalCount;

    const changes: Record<string, PartialBatchUpdateSubscriptions> = {};

    Object.values(subscriptionStates).forEach(subscription => {
      // Find the original subscription to determine the baseline state
      const originalSub = resource.sub_subscriptions?.find(
        sub => sub.subscription_id === subscription.subscription_id
      );

      if (!originalSub) return;

      // Calculate the current effective state (original + any global changes)
      const currentEffectiveState =
        globalChanges[subscription.subscription_id]?.enabled ?? originalSub.enabled;

      // Only create a change if it differs from the current effective state
      if (shouldEnableAll !== currentEffectiveState) {
        changes[subscription.subscription_id] = {
          subscription_id: subscription.subscription_id,
          resource_id: subscription.resource_id,
          enabled: shouldEnableAll,
        };
      }
    });

    setLocalChanges(changes);
  }, [subscriptionStates, resource.sub_subscriptions, globalChanges]);

  const handleComboboxSelect = useCallback(
    ({ value }: { value: string }) => {
      if (value === 'toggle-all') {
        handleToggleAll();
        return;
      }

      const subscription = subscriptionStates[value];
      if (subscription) {
        handleToggleSubscription(value, !subscription.enabled);
      }
    },
    [subscriptionStates, handleToggleAll, handleToggleSubscription]
  );

  const handleCancel = useCallback(() => {
    setLocalChanges({});
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm(localChanges);
    setLocalChanges({});
    onClose();
  }, [localChanges, onConfirm, onClose]);

  const hasChanges = Object.keys(localChanges).length > 0;

  return (
    <Modal
      open={isOpen}
      onOpenChange={open => !open && handleCancel()}
      style={{ width: 600 }}
      backdrop
      variant="settings"
    >
      <Settings variant="modal">
        <Settings.Main style={{ gap: 0 }}>
          <Settings.Main.Header style={{ flexShrink: 0 }}>
            <Box.Row gap="large">
              <Text.Headline size="xsmall">
                {resource.title}, {resourceContextLabel}
              </Text.Headline>

              <Box.Spacer />
              {hasChanges && (
                <Pill size="small" variant="alert">
                  {Object.keys(localChanges).length} pending changes
                </Pill>
              )}
            </Box.Row>
          </Settings.Main.Header>
          <Settings.Main.Content style={{ padding: `${4 * 4}px ${4 * 8}px` }}>
            <Combobox
              items={comboboxItems}
              onSelect={handleComboboxSelect}
              itemComponent={SubscriptionItem}
              cmdInputProps={{
                style: { marginLeft: 4 * 7, marginBottom: 4 * 2 },
                placeholder: `Search ${
                  resourceContextLabel?.toLowerCase() ?? resource.external_type ?? 'resources'
                }...`,
              }}
              style={{ minHeight: '300px', maxHeight: '500px' }}
            />
          </Settings.Main.Content>

          <Settings.Main.Footer>
            <Button key="cancel" priority="low" onClick={handleCancel}>
              Cancel
            </Button>
            <Box.Spacer />
            <Button key="confirm" priority="high" onClick={handleConfirm}>
              Done
            </Button>
          </Settings.Main.Footer>
        </Settings.Main>
      </Settings>
    </Modal>
  );
};
